<template>
  <div
    id="authentication-modal"
    tabindex="-1"
    aria-hidden="true"
    class="hidden overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 p-4 w-full md:inset-0 h-modal md:h-full"
  >
    <div class="relative w-full max-w-md h-full md:h-auto">
      <div class="relative bg-white rounded-lg shadow dark:bg-gray-700">
        <button
          type="button"
          class="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white"
          @click="closeModal"
        >
          <svg
            aria-hidden="true"
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          ><path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          /></svg>
          <span class="sr-only">{{ $t('landing.signup.close') }}</span>
        </button>
        <div class="py-6 px-6 lg:px-8">
          <h3 class="mb-4 text-xl font-medium text-gray-900 dark:text-white">
            {{ $t('landing.signup.title') }}
          </h3>
          <form
            class="space-y-6"
            @submit.prevent="submit"
          >
            <div>
              <label
                for="email"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >{{ $t('landing.signup.email') }}</label>
              <input
                id="email"
                type="email"
                name="email"
                autocomplete="email"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-400 focus:border-primary-400 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                placeholder="mail@example.com"
                required
              >
            </div>
            <div>
              <label
                for="password"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >{{ $t('landing.signup.password') }}</label>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="••••••••"
                minlength="8"
                autocomplete="current-password"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-400 focus:border-primary-400 block w-full p-2.5 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
                required
              >
            </div>
            <div class="flex justify-between">
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="remember"
                    type="checkbox"
                    value=""
                    class="w-4 h-4 bg-gray-50 rounded border border-gray-300 focus:ring-3 focus:ring-primary-400 dark:bg-gray-600 dark:border-gray-500 dark:focus:ring-primary-400 dark:ring-offset-gray-800"
                  >
                </div>
                <label
                  for="remember"
                  class="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                >{{ $t('landing.signup.remember') }}</label>
              </div>
              <a
                class="cursor-pointer text-sm text-primary-500 hover:underline dark:text-primary-400"
                @click="openForgotPasswordModal"
              >{{ $t('landing.signup.lostPassword') }}</a>
            </div>
            <div>
              <button
                :disabled="loading"
                type="submit"
                class="mb-4 w-full text-white bg-primary-500 hover:bg-primary-600 focus:ring-4 focus:outline-none focus:ring-primary-400 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-primary-400"
              >
                <svg
                  v-if="loading"
                  role="status"
                  class="inline mr-3 w-4 h-4 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
                {{ $t('landing.signup.login') }}
              </button>
              <div class="text-sm font-medium text-gray-500 dark:text-gray-300">
                {{ $t('landing.signup.noAccount') }} <a
                  class="text-primary-500 hover:underline dark:text-primary-400 cursor-pointer"
                  @click="openRegisterModal"
                >{{ $t('landing.signup.register') }}</a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    emits: {'openRegister': null, 'openForgotPassword': null},
    data() {
      return {
        modal: null,
        loading: false
      }
    },
    mounted() {
      this.modal = new Modal(document.querySelector('#authentication-modal'));
    },
    methods: {
      async submit() {
        this.loading = true;

        const response = await this.$store.dispatch('api/login', {
          email: document.querySelector('#email').value,
          password: document.querySelector('#password').value
        });
        if(response.status !== 201)  {
          this.loading = false;
          return;
        }
        
        const userResponse = await this.$store.dispatch('api/user', {
          token: response.data.token
        });
        if(!userResponse) {
          this.loading = false;
          return;
        }

        this.loading = false;

        // router to /d
        this.closeModal();
        this.$router.push({ name: 'dashboard' });
      },
      showModal() {
        this.modal.show();
        document.querySelector('#email').focus();
      },
      closeModal() {
        this.modal.hide();
      },
      openForgotPasswordModal() {
        this.closeModal();
        this.$emit('openForgotPassword');
      },
      openRegisterModal() {
        this.closeModal();
        this.$emit('openRegister');
      }
    }
  }
</script>
